import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Check, Sparkles, CreditCard, ArrowLeft, Loader2, CheckCircle2, Lock } from 'lucide-react';
import { useAuth, AuthProvider } from '../context/AuthContext';
import { API } from '../utils/api';
import { LanguageSelector } from '../components/LanguageSelector';

function SubscriptionPortalContent() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  // Check for return from Stripe Checkout
  const sessionId = searchParams.get('session_id');
  const status = searchParams.get('status');

  const fetchSubscription = useCallback(async () => {
    if (!auth.token) return;

    try {
      const res = await axios.get(`${API}/subscription/current`, { headers: auth.headers });
      setSubscription(res.data);
    } catch (e) {
      console.error('Failed to fetch subscription', e);
      if (e.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [auth.token, auth.headers, navigate]);

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setProcessingPayment(false);
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const res = await axios.get(`${API}/subscription/checkout-status/${sid}`, { headers: auth.headers });

      if (res.data.payment_status === 'paid') {
        setPaymentSuccess(true);
        setPaymentResult(res.data);
        setProcessingPayment(false);
        toast.success('Payment successful! Your subscription is now active.');
        fetchSubscription();
        return;
      } else if (res.data.status === 'expired') {
        setProcessingPayment(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setProcessingPayment(false);
      toast.error('Error checking payment status.');
    }
  }, [auth.headers, fetchSubscription]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Handle return from Stripe Checkout redirect
  useEffect(() => {
    if (sessionId && status === 'success') {
      setProcessingPayment(true);
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled.');
    }
  }, [sessionId, status, pollPaymentStatus]);

  const handleSelectPlan = async (plan) => {
    if (!plan || plan.id === subscription?.current_plan?.id) return;

    const cost = billingPeriod === 'annual' ? plan.cost_annual : plan.cost_monthly;

    // Free plan - no payment needed
    if (cost <= 0) {
      try {
        setProcessingPayment(true);
        const res = await axios.post(`${API}/subscription/create-checkout`, {
          plan_id: plan.id,
          billing_period: billingPeriod,
          origin_url: window.location.origin
        }, { headers: auth.headers });

        if (res.data.free_plan) {
          toast.success(`${plan.name} activated!`);
          setPaymentSuccess(true);
          setPaymentResult({ plan_name: plan.name, expires_at: res.data.expires_at });
          fetchSubscription();
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to activate plan');
      } finally {
        setProcessingPayment(false);
      }
      return;
    }

    setSelectedPlan(plan);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;

    try {
      setProcessingPayment(true);
      const res = await axios.post(`${API}/subscription/create-checkout`, {
        plan_id: selectedPlan.id,
        billing_period: billingPeriod,
        origin_url: window.location.origin
      }, { headers: auth.headers });

      if (res.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.url;
      } else if (res.data.free_plan) {
        setProcessingPayment(false);
        toast.success(`${selectedPlan.name} activated!`);
        setPaymentSuccess(true);
        setPaymentResult({ plan_name: selectedPlan.name, expires_at: res.data.expires_at });
        fetchSubscription();
      } else {
        setProcessingPayment(false);
        toast.error('Could not start checkout.');
      }
    } catch (e) {
      setProcessingPayment(false);
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const calcSavings = (monthly, annual) => {
    if (!monthly || monthly <= 0 || !annual) return 0;
    const yearlyFromMonthly = monthly * 12;
    if (yearlyFromMonthly <= 0 || annual <= 0) return 0;
    return Math.round(((yearlyFromMonthly - annual) / yearlyFromMonthly) * 100);
  };

  if (!auth.token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold mb-4">Please Login</h2>
            <p className="text-muted-foreground mb-4">You need to be logged in to manage your subscription.</p>
            <Button onClick={() => navigate('/')}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
          <p className="mt-2 text-muted-foreground">Loading subscription...</p>
        </div>
      </div>
    );
  }

  // Payment success view
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-4">
              Your subscription to <strong>{paymentResult?.plan_name}</strong> is now active.
            </p>
            {paymentResult?.expires_at && (
              <p className="text-sm text-muted-foreground mb-6">
                Valid until: {new Date(paymentResult.expires_at).toLocaleDateString()}
              </p>
            )}
            <Button onClick={() => navigate('/')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Processing payment view
  if (processingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-teal-600 mb-4" />
            <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { current_plan, subscription_end, plans } = subscription || {};
  const availablePlans = (plans || []).filter(p => p.is_enabled !== false);
  const maxSavings = Math.max(...availablePlans.map(p => calcSavings(p.cost_monthly, p.cost_annual)), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to App
            </Button>
            <h1 className="text-xl font-bold text-teal-700">PayMig Subscription</h1>
          </div>
          <LanguageSelector />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Plan */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>Your active subscription plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center p-4 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border">
              <div>
                <div className="font-bold text-xl flex items-center gap-2">
                  {current_plan?.name || 'Free'}
                  {current_plan?.cost_monthly === 0 ? (
                    <Badge variant="secondary">Free</Badge>
                  ) : (
                    <Badge variant="default">Premium</Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {current_plan?.max_transactions === -1 ? '∞ Unlimited' : current_plan?.max_transactions} transactions
                </div>
                {subscription_end && (
                  <div className="text-xs text-blue-600 mt-2">
                    Renews: {new Date(subscription_end).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Period Toggle */}
        <div className="flex items-center justify-center gap-4 p-4 bg-white rounded-lg shadow mb-6">
          <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-teal-600' : 'text-gray-500'}`}>
            Monthly
          </span>
          <Switch
            checked={billingPeriod === 'annual'}
            onCheckedChange={(checked) => setBillingPeriod(checked ? 'annual' : 'monthly')}
          />
          <span className={`text-sm font-medium ${billingPeriod === 'annual' ? 'text-teal-600' : 'text-gray-500'}`}>
            Annual
          </span>
          {maxSavings > 0 && (
            <Badge variant="default" className="bg-green-500 ml-2">
              <Sparkles className="h-3 w-3 mr-1" />
              Save up to {maxSavings}%
            </Badge>
          )}
        </div>

        {/* Plan Selection */}
        <h2 className="text-lg font-semibold mb-4 text-center">Choose Your Plan</h2>

        <div className="grid md:grid-cols-2 gap-4 mb-8 pb-28">
          {availablePlans.map(plan => {
            const savings = calcSavings(plan.cost_monthly, plan.cost_annual);
            const displayCost = billingPeriod === 'annual' ? plan.cost_annual : plan.cost_monthly;
            const isCurrentPlan = current_plan?.id === plan.id;
            const isFree = displayCost <= 0;
            const isSelected = selectedPlan?.id === plan.id;

            return (
              <Card
                key={plan.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  isCurrentPlan
                    ? 'border-teal-500 border-2 bg-teal-50'
                    : isSelected
                    ? 'border-blue-500 border-2 bg-blue-50'
                    : 'hover:border-teal-300'
                }`}
                onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
                data-testid={`plan-card-${plan.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="font-bold text-lg flex items-center gap-2">
                        {plan.name}
                        {plan.is_default && <Badge variant="outline">Popular</Badge>}
                      </div>
                      {plan.description && (
                        <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap line-clamp-2">
                          {plan.description}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-2xl text-teal-600">
                        {isFree ? 'Free' : `${displayCost} ${plan.currency || 'SEK'}`}
                      </div>
                      {!isFree && (
                        <div className="text-xs text-muted-foreground">
                          /{billingPeriod === 'annual' ? 'year' : 'month'}
                        </div>
                      )}
                      {billingPeriod === 'annual' && savings > 0 && (
                        <Badge variant="secondary" className="text-green-600 mt-1">
                          Save {savings}%
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.max_transactions === -1 ? 'Unlimited' : plan.max_transactions} transactions</span>
                    </div>
                    {plan.max_children && (
                      <div className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500" />
                        <span>{plan.max_children === -1 ? 'Unlimited' : plan.max_children} children</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500" />
                      <span>{plan.duration_days} days duration</span>
                    </div>
                  </div>

                  {isCurrentPlan ? (
                    <Badge variant="default" className="bg-teal-500 w-full justify-center py-2">
                      <Check className="h-3 w-3 mr-1" /> Current Plan
                    </Badge>
                  ) : (
                    <Button
                      variant={isSelected ? "default" : "outline"}
                      className={`w-full ${isSelected ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}
                    >
                      {isSelected
                        ? (<><Check className="h-4 w-4 mr-1" /> Selected</>)
                        : (isFree ? 'Select Free Plan' : 'Select Plan')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Checkout Button */}
        {selectedPlan && (
          <Card className="sticky bottom-4 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{selectedPlan.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {billingPeriod === 'annual' ? selectedPlan.cost_annual : selectedPlan.cost_monthly} {selectedPlan.currency || 'SEK'}/{billingPeriod === 'annual' ? 'year' : 'month'}
                  </p>
                </div>
                <div className="text-right">
                  <Button onClick={handleCheckout} size="lg" className="gap-2" data-testid="checkout-pay-button">
                    <CreditCard className="h-4 w-4" />
                    Continue to Payment
                  </Button>
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center justify-end gap-1">
                    <Lock className="h-3 w-3" /> Secured by Stripe
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

export function SubscriptionPortal() {
  const auth = useAuth();

  return (
    <AuthProvider value={auth}>
      <SubscriptionPortalContent />
    </AuthProvider>
  );
}
